import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  MenuItem
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ru } from 'date-fns/locale';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '../utils/api';

interface TemplateFile {
  name: string;
  path: string;
  created: string;
}

const TemplateGenerator: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [periodId, setPeriodId] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [templates, setTemplates] = useState<TemplateFile[]>([]);
  const [periods, setPeriods] = useState<any[]>([]);

  useEffect(() => {
    loadTemplates();
    loadPeriods();
  }, []);

  const loadTemplates = async () => {
    try {
      const data = await api.get('/generate-calendar/list');
      setTemplates(data);
    } catch (error) {
      console.error('Ошибка загрузки шаблонов:', error);
    }
  };

  const loadPeriods = async () => {
    try {
      const data = await api.get('/periods');
      setPeriods(data);
    } catch (error) {
      console.error('Ошибка загрузки периодов:', error);
    }
  };

  const handleGenerate = async () => {
    if (!selectedDate) {
      setError('Выберите дату');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const data = await api.post('/generate-calendar/generate', {
        date: selectedDate.toISOString().split('T')[0],
        periodId
      });

      setSuccess(`Шаблон успешно создан! Создано ${data.guidesCount} записей`);
      loadTemplates();

      window.open(`http://192.168.50.43:5001${data.filepath}`, '_blank');
    } catch (error: any) {
      setError(error.response?.data?.error || 'Ошибка генерации шаблона');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (filepath: string) => {
    window.open(`http://192.168.50.43:5001${filepath}`, '_blank');
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Генерация шаблонов календаря
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
          <Box sx={{ flex: { xs: '1 1 100%', md: '4 1 0%' } }}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ru}>
              <DatePicker
                label="Дата"
                value={selectedDate}
                onChange={(date) => setSelectedDate(date)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </LocalizationProvider>
          </Box>

          <Box sx={{ flex: { xs: '1 1 100%', md: '3 1 0%' } }}>
            <TextField
              select
              fullWidth
              label="Период"
              value={periodId}
              onChange={(e) => setPeriodId(Number(e.target.value))}
            >
              {periods.map((period) => (
                <MenuItem key={period.id} value={period.id}>
                  {period.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ flex: { xs: '1 1 100%', md: '3 1 0%' } }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleGenerate}
              disabled={loading || !selectedDate}
              fullWidth
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Генерация...' : 'Сгенерировать шаблон'}
            </Button>
          </Box>

          <Box sx={{ flex: { xs: '1 1 100%', md: '2 1 0%' } }}>
            <Button
              variant="outlined"
              onClick={loadTemplates}
              startIcon={<RefreshIcon />}
              fullWidth
            >
              Обновить
            </Button>
          </Box>
        </Box>
      </Paper>

      <Typography variant="h5" gutterBottom>
        Список шаблонов
      </Typography>

      {templates.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="textSecondary">
            Нет созданных шаблонов
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <List>
            {templates.map((template, index) => (
              <React.Fragment key={index}>
                <ListItem
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={() => handleDownload(template.path)}
                      sx={{ mr: 1 }}
                    >
                      <DownloadIcon />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={template.name}
                    secondary={`Создан: ${formatDate(template.created)}`}
                  />
                  <Chip
                    label="XLSX"
                    size="small"
                    color="primary"
                    sx={{ mr: 2 }}
                  />
                </ListItem>
                {index < templates.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default TemplateGenerator;